import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { authHeaders, errorMessage, unwrap } from 'api/call'
import {
  deleteMatchesById,
  getMatches,
  putMatchesById,
  type GetMatchesResponse,
  type MatchSearch,
} from 'api/generated'
import { toast } from 'sonner'

interface MatchesState {
  loading: boolean
  error: string | null
  matches: MatchSearch[] | null
  page: number
  totalPages: number
}

const initialState: MatchesState = {
  loading: false,
  error: null,
  matches: null,
  page: 1,
  totalPages: 0,
}

// GET /matches doesn't return the current page: keep the requested one
export const fetchMatches = createAsyncThunk<
  GetMatchesResponse & { page: number },
  number,
  { rejectValue: string }
>('matches/fetchMatches', async (page, { rejectWithValue }) => {
  try {
    const response = await unwrap(
      getMatches({ headers: authHeaders(), query: { page } })
    )
    return { ...response, page }
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch matches.'))
  }
})

// Only the result is editable from the backoffice table
export const updateMatch = createAsyncThunk<
  MatchSearch,
  MatchSearch,
  { rejectValue: string }
>('matches/updateMatch', async (match, { rejectWithValue }) => {
  try {
    await unwrap(
      putMatchesById({
        headers: authHeaders(),
        path: { id: match.id },
        body: { result: match.result ?? undefined },
      })
    )
    toast.success('Partita aggiornata con successo!')
    return match
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to update match.'))
  }
})

export const deleteMatch = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('matches/deleteMatch', async (matchId, { rejectWithValue }) => {
  try {
    await unwrap(
      deleteMatchesById({ headers: authHeaders(), path: { id: matchId } })
    )
    toast.success('Partita eliminata con successo!')
    return matchId
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to delete match.'))
  }
})

const matchesSlice = createSlice({
  name: 'matches',
  initialState,
  reducers: {
    setMatchResult: (
      state,
      action: PayloadAction<{ matchId: number; result: string }>
    ) => {
      state.matches =
        state.matches?.map((match) =>
          match.id === action.payload.matchId
            ? { ...match, result: action.payload.result }
            : match
        ) ?? null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatches.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.loading = false
        state.matches = action.payload.matches
        state.page = action.payload.page
        state.totalPages = action.payload.total_pages
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch matches.'
      })
      .addCase(updateMatch.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMatch.fulfilled, (state, action) => {
        state.loading = false
        state.matches =
          state.matches?.map((match) =>
            match.id === action.payload.id ? action.payload : match
          ) ?? null
      })
      .addCase(updateMatch.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update match.'
      })
      .addCase(deleteMatch.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteMatch.fulfilled, (state, action) => {
        state.loading = false
        state.matches =
          state.matches?.filter((match) => match.id !== action.payload) ?? null
      })
      .addCase(deleteMatch.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete match.'
      })
  },
})

export const { setMatchResult } = matchesSlice.actions

export const matchesReducer = matchesSlice.reducer
